// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SuperadminService } from "@/services/superadmin";
import { ComposeNotification } from "@/components/superadmin/notifications/ComposeNotification";
import { NotificationHistory } from "@/components/superadmin/notifications/NotificationHistory";

type NotificationTab = "compose" | "history";

const validTabs: NotificationTab[] = ["compose", "history"];

export interface SearchParams {
  tab: NotificationTab;
  historyPage: number;
}

export const Route = createFileRoute("/superadmin/notifications")({
  component: NotificationsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    tab: (validTabs.includes(search.tab as NotificationTab)
      ? search.tab
      : "compose") as NotificationTab,
    historyPage: Number(search.historyPage) || 1,
  }),
});

function NotificationsPage() {
  const { t } = useTranslation("superadmin");
  const navigate = useNavigate();
  const params = Route.useSearch();

  const updateParams = useCallback(
    (updates: Partial<SearchParams>) => {
      navigate({
        to: "/superadmin/notifications",
        search: { ...params, ...updates },
        replace: true,
      });
    },
    [navigate, params],
  );

  const handleTabChange = useCallback(
    (newTab: string) => {
      updateParams({ tab: newTab as NotificationTab });
    },
    [updateParams],
  );

  const historyQuery = useQuery({
    queryKey: ["superadmin", "notificationHistory", params.historyPage],
    queryFn: () =>
      SuperadminService.getNotificationHistory({ page: params.historyPage }),
    enabled: params.tab === "history",
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("notifications.title")}</h1>
        <p className="text-muted-foreground mt-1">
          {t("notifications.description")}
        </p>
      </div>

      <Tabs value={params.tab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="compose">
            {t("notifications.tabs.compose")}
          </TabsTrigger>
          <TabsTrigger value="history">
            {t("notifications.tabs.history")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="compose" className="mt-6">
          <ComposeNotification />
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <NotificationHistory
            data={historyQuery.data}
            isLoading={historyQuery.isLoading}
            page={params.historyPage}
            onPageChange={(page) => updateParams({ historyPage: page })}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
